import {
  Link,
  DialogTitle,
  DialogActions,
  alpha,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  ListSubheader,
  MenuItem,
  Popover,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import MoreVertIcon from "@mui/icons-material/MoreVertOutlined";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphoneOutlined";
import { useEffect, useRef, useState } from "react";
import type { Room } from "@shared/types/foyer";
import {
  foyerApi,
  type RoomTypeRecord,
  type PhotoMailboxInfo,
  SCREEN_CATEGORIES,
  SCREEN_CATEGORY_LABELS,
} from "../http/foyer";

interface Props {
  rooms: Room[];
  onChange: (next: Room[]) => void;
  /**
   * When set, occupancy toggles + bulk check-in/out call the server
   * directly (immediate persist). Without it the column is hidden - used
   * by tests / new-premise creation flows where there's no slug yet.
   */
  premiseId?: string;
  /** When "care", shows the family-photos mailbox icon per room. */
  segment?: "hotel" | "care";
}

export default function RoomsEditor({
  rooms,
  onChange,
  premiseId,
  segment,
}: Props) {
  const [rangeText, setRangeText] = useState("");
  const [pickedType, setPickedType] = useState<string>("");
  const [addError, setAddError] = useState<string | null>(null);
  const [roomTypes, setRoomTypes] = useState<RoomTypeRecord[]>([]);
  /** The PALETTE: what this premise licensed. null while loading. */
  const [palette, setPalette] = useState<
    { id: string; name: string; rooms: number }[] | null
  >(null);
  /** What each room is assigned, keyed by room number. */
  const [assigned, setAssigned] = useState<Map<string, string[]>>(new Map());
  const [assignError, setAssignError] = useState<string | null>(null);
  /**
   * Which room's Content menu is open, if any.
   *
   * Controlled so it can be CLOSED ON PICK. A `multiple` MUI select stays open
   * by design — you might want several — but the common case here is one
   * product per room, and a menu that lingers over the row you were just
   * looking at has to be dismissed before you can see what you did. Reported
   * by Sigge.
   *
   * The cost is honest: giving one room two products means opening it twice.
   * That is the right trade when almost every room gets exactly one.
   */
  const [openMenuFor, setOpenMenuFor] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  /** Which product a bulk assign is currently writing, if any. */
  const [bulkProduct, setBulkProduct] = useState<string | null>(null);

  useEffect(() => {
    if (!premiseId) return;
    let alive = true;
    void foyerApi
      .premiseEntitlement(premiseId)
      .then((r) => {
        if (!alive) return;
        setPalette(r.featureSets);
        setAssigned(new Map(r.rooms.map((x) => [x.roomNumber, x.products])));
      })
      // An empty palette rather than a broken panel: the rooms table is the
      // point of this screen and must not be held hostage by what sits above.
      .catch(() => {
        if (alive) setPalette([]);
      });
    return () => {
      alive = false;
    };
  }, [premiseId]);
  const [moreAnchor, setMoreAnchor] = useState<{
    el: HTMLElement;
    idx: number;
  } | null>(null);
  const [occupancyBusy, setOccupancyBusy] = useState<Set<string>>(new Set());
  // Rooms with an active guest-companion token - a lightweight indicator only.
  // Companion sessions are never Fleet devices or rooms-list rows.
  const [companionRooms, setCompanionRooms] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [occupancyError, setOccupancyError] = useState<string | null>(null);
  // On a care premise occupancy is DERIVED: admitting a resident fills the
  // room, discharging empties it. Hand-toggling it here would desync the flag
  // from the Occupancy record - the server refuses it with 409 - so the column
  // is read-only and points at the flow that actually owns it.
  const occupancyDerived = segment === "care";
  const [photoAnchor, setPhotoAnchor] = useState<{
    el: HTMLElement;
    roomId: string;
  } | null>(null);
  // Map<roomId, PhotoMailboxInfo> - loaded once when the editor mounts for a
  // care premise so the camera dot indicator can be shown without extra clicks.
  const [mailboxes, setMailboxes] = useState<Map<string, PhotoMailboxInfo>>(
    new Map()
  );

  useEffect(() => {
    foyerApi
      .listRoomTypes()
      .then((list) => setRoomTypes(Array.isArray(list) ? list : []))
      .catch(() => setRoomTypes([]));
  }, []);

  // Which rooms have an active guest-companion token (indicator badge only).
  useEffect(() => {
    if (!premiseId) return;
    foyerApi
      .guestCompanionRooms(premiseId)
      .then((res) => {
        setCompanionRooms(res.enabled ? new Set(res.roomIds) : new Set());
      })
      .catch(() => {
        /* non-fatal: badge just won't show */
      });
  }, [premiseId]);

  // Load mailboxes for the premise whenever care segment is active.
  useEffect(() => {
    if (!premiseId || segment !== "care") return;
    foyerApi
      .listRoomPhotoMailboxes(premiseId)
      .then((list) => {
        const m = new Map<string, PhotoMailboxInfo>();
        for (const mb of list) m.set(mb.roomId, mb);
        setMailboxes(m);
      })
      .catch(() => {
        /* non-fatal: dot just won't show */
      });
  }, [premiseId, segment]);

  function addRooms() {
    const parsed = parseRange(rangeText);
    if (parsed.error) {
      setAddError(parsed.error);
      return;
    }
    setAddError(null);
    const existing = new Set(rooms.map((r) => r.id));
    const additions: Room[] = parsed.ids
      .filter((id) => !existing.has(id))
      .map((id) => ({
        id,
        ...(pickedType ? { roomType: pickedType } : {}),
      }));
    if (additions.length === 0) {
      setAddError("All rooms in that range already exist.");
      return;
    }
    onChange([...rooms, ...additions]);
    setRangeText("");
  }

  function updateRoom(idx: number, patch: Partial<Room>) {
    const next = [...rooms];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  }

  function deleteRoom(idx: number) {
    onChange(rooms.filter((_, i) => i !== idx));
  }

  async function setOccupancy(idx: number, occupied: boolean) {
    const room = rooms[idx];
    if (!premiseId) return;
    setOccupancyBusy((s) => new Set(s).add(room.id));
    setOccupancyError(null);
    try {
      await foyerApi.setRoomOccupancy(premiseId, room.id, occupied);
      const next = [...rooms];
      next[idx] = { ...next[idx], occupied };
      onChange(next);
    } catch (e) {
      // Leave the prior state and tell the operator - a silent failure
      // here looks like a stuck switch.
      setOccupancyError(
        `Couldn't ${occupied ? "check in" : "check out"} room ${room.id}: ${
          e instanceof Error ? e.message : String(e)
        }`
      );
    } finally {
      setOccupancyBusy((s) => {
        const c = new Set(s);
        c.delete(room.id);
        return c;
      });
    }
  }

  async function bulkOccupancy(occupied: boolean) {
    if (!premiseId) return;
    setBulkBusy(true);
    setOccupancyError(null);
    try {
      if (occupied) await foyerApi.checkInAll(premiseId);
      else await foyerApi.checkOutAll(premiseId);
      onChange(rooms.map((r) => ({ ...r, occupied })));
    } catch (e) {
      setOccupancyError(
        `Couldn't ${occupied ? "check in" : "check out"} all rooms: ${
          e instanceof Error ? e.message : String(e)
        }`
      );
    } finally {
      setBulkBusy(false);
    }
  }

  const typeNameById = new Map(roomTypes.map((t) => [t.id, t.name]));

  /**
   * Give one product to every room, or take it off every room.
   *
   * 🚨 A UNION, not a replacement. Each room's payload is its existing
   * assignment plus (or minus) this one product — sending only the product
   * would silently strip everything else those rooms hold, which is the
   * full-replacement mistake this model already made once with room types.
   *
   * Rooms that already agree are still sent. The server writes the same value
   * and recompiles once, and filtering them out here would mean maintaining a
   * second opinion about what "already correct" means.
   */
  async function bulkAssign(productId: string, give: boolean) {
    if (!premiseId) return;
    setBulkProduct(productId);
    setAssignError(null);
    try {
      const assignments = rooms.map((r) => {
        const current = assigned.get(r.id) ?? [];
        const next = give
          ? [...new Set([...current, productId])]
          : current.filter((id) => id !== productId);
        return { roomNumber: r.id, products: next.sort() };
      });
      await foyerApi.assignRoomProducts(premiseId, assignments);
      const fresh = await foyerApi.premiseEntitlement(premiseId);
      setPalette(fresh.featureSets);
      setAssigned(new Map(fresh.rooms.map((x) => [x.roomNumber, x.products])));
    } catch (e) {
      setAssignError(e instanceof Error ? e.message : String(e));
    } finally {
      setBulkProduct(null);
    }
  }

  /**
   * Assign products to ONE room.
   *
   * Sends the complete list for that room, because the server takes the whole
   * field — a partial one silently revokes the rest — and this control already
   * knows the full answer.
   *
   * Optimistic, then reconciled from the server's reply: a checkbox that waits
   * for a round trip before moving feels broken, and a checkbox that moves and
   * then lies is worse. On refusal it is put back and the reason is shown.
   */
  async function setRoomProducts(roomNumber: string, products: string[]) {
    if (!premiseId) return;
    const previous = assigned.get(roomNumber) ?? [];
    setAssigned((m) => new Map(m).set(roomNumber, products));
    setAssignError(null);
    try {
      await foyerApi.assignRoomProducts(premiseId, [{ roomNumber, products }]);
      const fresh = await foyerApi.premiseEntitlement(premiseId);
      setPalette(fresh.featureSets);
      setAssigned(new Map(fresh.rooms.map((x) => [x.roomNumber, x.products])));
    } catch (e) {
      setAssigned((m) => new Map(m).set(roomNumber, previous));
      setAssignError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
      >
        <Typography variant="h6">Rooms</Typography>
        {premiseId && rooms.length > 0 && !occupancyDerived && (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              disabled={bulkBusy}
              onClick={() => bulkOccupancy(true)}
              data-testid="rooms-checkin-all"
            >
              Check in all
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={bulkBusy}
              onClick={() => bulkOccupancy(false)}
              data-testid="rooms-checkout-all"
            >
              Check out all
            </Button>
          </Stack>
        )}
      </Stack>
      {occupancyError && (
        <Alert severity="error" onClose={() => setOccupancyError(null)}>
          {occupancyError}
        </Alert>
      )}
      {/**
       * What these rooms PLAY — shown once, not per row.
       *
       * Entitlement is a fact about the PREMISE now: every room plays what the
       * customer bought, so repeating it on 200 rows would be 200 copies of
       * one sentence. Reported by Sigge, who added a product to a premise and
       * then could not find it anywhere near the rooms — the CMS showed a
       * room's name, type and occupancy, and nothing about content.
       *
       * Read from the server's own compiler, so it cannot claim something the
       * rooms do not hold.
       */}
      {premiseId && (
        <Alert
          severity={palette === null || palette.length ? "info" : "warning"}
          sx={{ mb: 2 }}
          data-testid="rooms-entitlement"
        >
          {palette === null ? (
            "Checking what this premise licensed…"
          ) : palette.length ? (
            <>
              Licensed here:{" "}
              {palette.map((f, i) => (
                <span key={f.id}>
                  {i > 0 ? ", " : ""}
                  <strong>{f.name}</strong>{" "}
                  <Typography component="span" variant="caption">
                    ({f.rooms} {f.rooms === 1 ? "room" : "rooms"})
                  </Typography>
                </span>
              ))}
              .{" "}
              {/* The action belongs to a PRODUCT, not to this sentence.
                  "Click here for bulk" reads fine with one product and makes
                  somebody guess with three — so the link opens a dialog with a
                  row per product, which reads the same at any number. */}
              <Link
                component="button"
                type="button"
                onClick={() => setBulkOpen(true)}
                data-testid="rooms-bulk-open"
              >
                Assign in bulk
              </Link>
              , or pick per room in the Content column.
            </>
          ) : (
            "Nothing licensed to this premise yet, so there is nothing to give a room. Add a channel product under Products."
          )}
        </Alert>
      )}

      {assignError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setAssignError(null)}
        >
          {assignError}
        </Alert>
      )}

      {rooms.length > 0 && (
        <TableContainer>
          <Table size="small" data-testid="rooms-list" sx={{ width: "100%" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 110 }}>Room</TableCell>
                <TableCell>Name</TableCell>
                <TableCell sx={{ width: 180 }}>Type</TableCell>
                {premiseId && (
                  <TableCell sx={{ width: 220 }}>Content</TableCell>
                )}
                {premiseId && (
                  <TableCell sx={{ width: 90 }} align="center">
                    Occupied
                  </TableCell>
                )}
                <TableCell sx={{ width: 148 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rooms.map((room, i) => (
                <TableRow
                  key={room.id}
                  data-testid={`room-row-${room.id}`}
                  hover
                >
                  <TableCell sx={{ py: 0.5 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <TextField
                        value={room.id}
                        onChange={(e) => updateRoom(i, { id: e.target.value })}
                        size="small"
                        variant="standard"
                        fullWidth
                        inputProps={{ "data-testid": `room-id-${room.id}` }}
                      />
                      {companionRooms.has(room.id) && (
                        <Tooltip title="Guest companion active in this room">
                          <PhoneIphoneIcon
                            fontSize="small"
                            color="primary"
                            data-testid={`room-companion-${room.id}`}
                          />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <TextField
                      value={room.name ?? ""}
                      onChange={(e) =>
                        updateRoom(i, { name: e.target.value || undefined })
                      }
                      size="small"
                      variant="standard"
                      placeholder="Name (optional)"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <TextField
                      select
                      value={room.roomType ?? ""}
                      onChange={(e) =>
                        updateRoom(i, {
                          roomType: e.target.value || undefined,
                        })
                      }
                      size="small"
                      variant="standard"
                      fullWidth
                      SelectProps={{
                        displayEmpty: true,
                        renderValue: (val) => {
                          if (!val)
                            return (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontStyle="italic"
                              >
                                Untyped
                              </Typography>
                            );
                          return typeNameById.get(val as string) ?? "(unknown)";
                        },
                      }}
                      inputProps={{
                        "data-testid": `room-type-${room.id}`,
                      }}
                    >
                      <MenuItem value="">
                        <em>Untyped</em>
                      </MenuItem>
                      {/* Grouped by screen category (Hotel/Care, Common
                          area, Public viewing) - the SAME connector a
                          licence's own category picker uses - so which
                          licence pool a type draws from is visible right
                          here, without collapsing named types into three
                          generic ones. A care facility keeps its named
                          wings (Memory Care, Assisted Living, …) for
                          call-contact routing; a hotel with no need for
                          them just has one type per category. */}
                      {SCREEN_CATEGORIES.flatMap((cat) => {
                        const inCategory = roomTypes.filter(
                          (t) => t.category === cat
                        );
                        if (inCategory.length === 0) return [];
                        return [
                          <ListSubheader key={`cat-${cat}`}>
                            {SCREEN_CATEGORY_LABELS[cat]}
                          </ListSubheader>,
                          ...inCategory.map((t) => (
                            <MenuItem key={t.id} value={t.id}>
                              {t.name}
                            </MenuItem>
                          )),
                        ];
                      })}
                      {(() => {
                        const known: readonly string[] = SCREEN_CATEGORIES;
                        const uncategorised = roomTypes.filter(
                          (t) => !known.includes(t.category)
                        );
                        if (uncategorised.length === 0) return null;
                        return [
                          <ListSubheader key="cat-other">Other</ListSubheader>,
                          ...uncategorised.map((t) => (
                            <MenuItem key={t.id} value={t.id}>
                              {t.name}
                            </MenuItem>
                          )),
                        ];
                      })()}
                    </TextField>
                  </TableCell>
                  {premiseId && (
                    <TableCell sx={{ py: 0.5 }}>
                      {/* Only what the premise LICENSED is offered: a licence
                          is a palette, and the server refuses anything else
                          anyway. Offering more would be a checkbox that fails
                          on save. */}
                      <TextField
                        select
                        size="small"
                        fullWidth
                        disabled={!palette || palette.length === 0}
                        value={assigned.get(room.id) ?? []}
                        onChange={(e) => {
                          // MUI hands a multiple-select its value as an array,
                          // but types it as the input's string. Autofill can
                          // still deliver a comma-joined string, so both.
                          const v = e.target.value as unknown as
                            | string
                            | string[];
                          setOpenMenuFor(null);
                          void setRoomProducts(
                            room.id,
                            typeof v === "string"
                              ? v.split(",").filter(Boolean)
                              : v
                          );
                        }}
                        SelectProps={{
                          multiple: true,
                          displayEmpty: true,
                          open: openMenuFor === room.id,
                          onOpen: () => setOpenMenuFor(room.id),
                          onClose: () => setOpenMenuFor(null),
                          renderValue: (v: unknown) => {
                            const ids = v as string[];
                            if (!palette?.length) return "Nothing licensed";
                            if (ids.length === 0) return "None";
                            return ids
                              .map(
                                (id) =>
                                  palette.find((f) => f.id === id)?.name ?? id
                              )
                              .join(", ");
                          },
                        }}
                        inputProps={{
                          "data-testid": `room-products-${room.id}`,
                        }}
                      >
                        {(palette ?? []).map((f) => (
                          <MenuItem key={f.id} value={f.id}>
                            {f.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                  )}
                  {premiseId && (
                    <TableCell sx={{ py: 0.5 }} align="center">
                      <Switch
                        size="small"
                        checked={!!room.occupied}
                        disabled={
                          occupancyDerived || occupancyBusy.has(room.id)
                        }
                        onChange={(e) => setOccupancy(i, e.target.checked)}
                        inputProps={
                          {
                            "aria-label": `Occupied ${room.id}`,
                            "data-testid": `room-occupied-${room.id}`,
                          } as React.InputHTMLAttributes<HTMLInputElement>
                        }
                      />
                    </TableCell>
                  )}
                  <TableCell
                    align="right"
                    sx={{ py: 0.5, whiteSpace: "nowrap" }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="flex-end"
                    >
                      {premiseId && segment === "care" && (
                        <Tooltip
                          title={
                            mailboxes.has(room.id)
                              ? "Manage family photos"
                              : "Set up family photos"
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={(e) =>
                              setPhotoAnchor({
                                el: e.currentTarget,
                                roomId: room.id,
                              })
                            }
                            data-testid={`room-photo-mailbox-${room.id}`}
                            aria-label="Family photos email"
                            sx={{
                              color: mailboxes.get(room.id)?.active
                                ? "success.contrastText"
                                : "text.secondary",
                              ...(mailboxes.get(room.id)?.active && {
                                bgcolor: "success.main",
                                boxShadow: (t) =>
                                  `0 0 0 2px ${alpha(
                                    t.palette.success.main,
                                    0.35
                                  )}`,
                                "&:hover": {
                                  bgcolor: "success.dark",
                                },
                              }),
                            }}
                          >
                            <Box sx={{ position: "relative", display: "flex" }}>
                              <PhotoCameraIcon fontSize="small" />
                              {mailboxes.has(room.id) &&
                                !mailboxes.get(room.id)?.active && (
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      top: -2,
                                      right: -2,
                                      width: 9,
                                      height: 9,
                                      borderRadius: "50%",
                                      border: "1.5px solid",
                                      borderColor: "background.paper",
                                      bgcolor: "warning.main",
                                    }}
                                    data-testid={`room-has-mailbox-${room.id}`}
                                  />
                                )}
                              {mailboxes.get(room.id)?.active && (
                                <Box
                                  sx={{
                                    position: "absolute",
                                    top: -3,
                                    right: -3,
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    border: "1.5px solid",
                                    borderColor: "success.main",
                                    bgcolor: "success.contrastText",
                                  }}
                                  data-testid={`room-has-mailbox-${room.id}`}
                                />
                              )}
                            </Box>
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton
                        size="small"
                        onClick={(e) =>
                          setMoreAnchor({ el: e.currentTarget, idx: i })
                        }
                        data-testid={`room-more-${room.id}`}
                        aria-label="More room options"
                        sx={{
                          color: room.integrationId
                            ? "success.contrastText"
                            : "text.secondary",
                          ...(room.integrationId && {
                            bgcolor: "success.main",
                            boxShadow: (t) =>
                              `0 0 0 2px ${alpha(t.palette.success.main, 0.35)}`,
                            "&:hover": {
                              bgcolor: "success.dark",
                            },
                          }),
                        }}
                      >
                        <Box sx={{ position: "relative", display: "flex" }}>
                          <MoreVertIcon fontSize="small" />
                          {room.integrationId && (
                            <Box
                              sx={{
                                position: "absolute",
                                top: -3,
                                right: -3,
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                border: "1.5px solid",
                                borderColor: "success.main",
                                bgcolor: "success.contrastText",
                              }}
                              data-testid={`room-has-integration-${room.id}`}
                            />
                          )}
                        </Box>
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => deleteRoom(i)}
                        data-testid={`delete-room-${room.id}`}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/**
       * Bulk assignment, a ROW PER PRODUCT.
       *
       * Sigge asked for "click here for bulk" in the summary line and then
       * asked the right follow-up: what does that copy say when there are
       * several products? Nothing good — "bulk" is meaningless without naming
       * one, and a sentence cannot name three. A row per product reads the
       * same at any number, and leaves somewhere for "give it to THESE rooms"
       * to live later without another redesign.
       */}
      <Dialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Assign in bulk
          <Typography variant="body2" color="text.secondary">
            Every room here, at once. Picking per room stays available in the
            Content column.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            {(palette ?? []).map((f) => (
              <Stack
                key={f.id}
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {f.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {f.rooms} of {rooms.length}{" "}
                    {rooms.length === 1 ? "room" : "rooms"}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={bulkProduct !== null || f.rooms === rooms.length}
                    onClick={() => void bulkAssign(f.id, true)}
                    data-testid={`bulk-all-${f.id}`}
                  >
                    Give to all
                  </Button>
                  <Button
                    size="small"
                    color="inherit"
                    disabled={bulkProduct !== null || f.rooms === 0}
                    onClick={() => void bulkAssign(f.id, false)}
                    data-testid={`bulk-none-${f.id}`}
                  >
                    Take off all
                  </Button>
                </Stack>
              </Stack>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {rooms.length === 0 && (
        <Typography color="text.secondary" data-testid="empty-rooms">
          No rooms yet.
        </Typography>
      )}

      <Stack
        spacing={1}
        sx={{
          p: 2,
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          Add rooms
        </Typography>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <TextField
            value={rangeText}
            onChange={(e) => {
              setRangeText(e.target.value);
              if (addError) setAddError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addRooms();
              }
            }}
            size="small"
            placeholder="101-110, 201, 305-308"
            helperText="Single number, range like 101-110, or comma-separated mix"
            sx={{ flex: 2 }}
            inputProps={{ "data-testid": "new-room-id" }}
            error={Boolean(addError)}
          />
          <TextField
            select
            label="Type"
            value={pickedType}
            onChange={(e) => setPickedType(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 160 }}
            SelectProps={{
              displayEmpty: true,
              renderValue: (val) => {
                if (!val)
                  return (
                    <Typography variant="body2" color="text.secondary">
                      Untyped
                    </Typography>
                  );
                return typeNameById.get(val as string) ?? "(unknown)";
              },
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ "data-testid": "new-room-type" }}
          >
            <MenuItem value="">
              <em>Untyped</em>
            </MenuItem>
            {/* Same grouping as the per-row picker below - see its comment. */}
            {SCREEN_CATEGORIES.flatMap((cat) => {
              const inCategory = roomTypes.filter((t) => t.category === cat);
              if (inCategory.length === 0) return [];
              return [
                <ListSubheader key={`cat-${cat}`}>
                  {SCREEN_CATEGORY_LABELS[cat]}
                </ListSubheader>,
                ...inCategory.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                )),
              ];
            })}
          </TextField>
          <Button
            startIcon={<AddIcon />}
            onClick={addRooms}
            variant="outlined"
            data-testid="add-room"
            sx={{ alignSelf: "flex-start" }}
          >
            Add
          </Button>
        </Stack>
        {addError && (
          <Typography variant="caption" color="error">
            {addError}
          </Typography>
        )}
        {roomTypes.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            No room types defined yet - admins can create them under{" "}
            <strong>Room types</strong> in the left nav.
          </Typography>
        )}
      </Stack>

      {moreAnchor && (
        <IntegrationIdPopover
          anchorEl={moreAnchor.el}
          room={rooms[moreAnchor.idx]}
          onClose={() => setMoreAnchor(null)}
          onSave={(value) => {
            updateRoom(moreAnchor.idx, { integrationId: value || undefined });
            setMoreAnchor(null);
          }}
        />
      )}
      {photoAnchor && premiseId && (
        <PhotoMailboxPopover
          anchorEl={photoAnchor.el}
          premiseId={premiseId}
          roomId={photoAnchor.roomId}
          existing={mailboxes.get(photoAnchor.roomId) ?? null}
          onClose={() => setPhotoAnchor(null)}
          onMailboxChange={(mb) => {
            setMailboxes((prev) => {
              const next = new Map(prev);
              if (mb) next.set(mb.roomId, mb);
              else next.delete(photoAnchor.roomId);
              return next;
            });
          }}
        />
      )}
    </Stack>
  );
}

/**
 * Parse a range expression into a list of room ids. Supports:
 *   - "101"                → ["101"]
 *   - "101-105"            → ["101","102","103","104","105"]
 *   - "1-5, 10, 21-22"     → ["1","2","3","4","5","10","21","22"]
 * Numbers in a range are zero-padded to match the width of the upper
 * bound when both endpoints have the same length ("001-009" → "001"..).
 */
function parseRange(text: string): { ids: string[]; error: string | null } {
  const trimmed = text.trim();
  if (!trimmed) return { ids: [], error: "Enter a room number or range." };
  const seen = new Set<string>();
  const out: string[] = [];
  for (const segmentRaw of trimmed.split(",")) {
    const segment = segmentRaw.trim();
    if (!segment) continue;
    const m = /^(\d+)\s*-\s*(\d+)$/.exec(segment);
    if (m) {
      const start = Number(m[1]);
      const end = Number(m[2]);
      if (start > end) {
        return {
          ids: [],
          error: `Range start (${start}) is higher than end (${end}).`,
        };
      }
      if (end - start > 999) {
        return {
          ids: [],
          error: "Range is too large (>1000 rooms). Add in smaller chunks.",
        };
      }
      const pad = m[1].length === m[2].length ? m[1].length : 0;
      for (let n = start; n <= end; n++) {
        const id = pad > 0 ? String(n).padStart(pad, "0") : String(n);
        if (!seen.has(id)) {
          seen.add(id);
          out.push(id);
        }
      }
    } else {
      // Plain id (digits or anything else - accept whatever the user types)
      if (!seen.has(segment)) {
        seen.add(segment);
        out.push(segment);
      }
    }
  }
  return { ids: out, error: null };
}

/**
 * Popover for managing a room's family-photos mailbox.
 *
 * States:
 *   - No mailbox yet  → single "Activate" button that creates one.
 *   - Mailbox exists  → full management view: email+copy, stats, allowlist,
 *                       pause toggle, revoke button.
 */
function PhotoMailboxPopover({
  anchorEl,
  premiseId,
  roomId,
  existing,
  onClose,
  onMailboxChange,
}: {
  anchorEl: HTMLElement;
  premiseId: string;
  roomId: string;
  existing: PhotoMailboxInfo | null;
  onClose: () => void;
  /** Called when mailbox is created, updated, or revoked (null = revoked). */
  onMailboxChange: (mb: PhotoMailboxInfo | null) => void;
}) {
  const [mailbox, setMailbox] = useState<PhotoMailboxInfo | null>(existing);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // All photos for this room (all statuses) - used for stats + thumbnail grid.
  type RoomPhoto = {
    photoId: string;
    roomId: string;
    receivedAt: string;
    expiresAt: string;
    status: string;
    imageUrl: string;
    fromName: string;
    fromEmail: string;
    subject: string;
    caption: string | null;
  };
  const [roomPhotos, setRoomPhotos] = useState<RoomPhoto[]>([]);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  // Index (into roomPhotos) of the photo opened in the large-format
  // lightbox; null = closed.
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Allowlist editing.
  const [allowlistText, setAllowlistText] = useState<string>("");
  const [allowlistDirty, setAllowlistDirty] = useState(false);
  const [savingAllowlist, setSavingAllowlist] = useState(false);

  // Revoke confirmation step.
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [revoking, setRevoking] = useState(false);

  // When mailbox is known, load all photos and set up allowlist editor.
  useEffect(() => {
    if (!mailbox) return;
    setAllowlistText(mailbox.allowlist.join("\n"));
    foyerApi
      .listPremiseCarePhotos(premiseId, { roomId })
      .then((photos) => setRoomPhotos(photos))
      .catch(() => {
        /* non-critical */
      });
  }, [mailbox, premiseId, roomId]);

  // Stats derived from approved photos only (what's visible on the TV).
  const approvedPhotos = roomPhotos.filter((p) => p.status === "approved");
  const photoCount = approvedPhotos.length;
  const lastReceived =
    approvedPhotos.length > 0
      ? approvedPhotos.reduce((a, b) => (a.receivedAt > b.receivedAt ? a : b))
          .receivedAt
      : null;

  function copyEmail() {
    if (!mailbox?.emailAddress) return;
    void navigator.clipboard.writeText(mailbox.emailAddress).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  async function activate() {
    setLoading(true);
    setError(null);
    try {
      const mb = await foyerApi.upsertRoomPhotoMailbox(premiseId, roomId);
      setMailbox(mb);
      onMailboxChange(mb);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not create mailbox");
    } finally {
      setLoading(false);
    }
  }

  async function toggleActive() {
    if (!mailbox) return;
    try {
      const mb = await foyerApi.updateRoomPhotoMailbox(premiseId, roomId, {
        active: !mailbox.active,
      });
      setMailbox(mb);
      onMailboxChange(mb);
    } catch {
      /* show nothing - non-critical */
    }
  }

  async function toggleModerated() {
    if (!mailbox) return;
    try {
      const mb = await foyerApi.updateRoomPhotoMailbox(premiseId, roomId, {
        moderated: !mailbox.moderated,
      });
      setMailbox(mb);
      onMailboxChange(mb);
    } catch {
      /* show nothing - non-critical */
    }
  }

  async function saveAllowlist() {
    if (!mailbox) return;
    setSavingAllowlist(true);
    try {
      const emails = allowlistText
        .split("\n")
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
      const mb = await foyerApi.updateRoomPhotoMailbox(premiseId, roomId, {
        allowlist: emails,
      });
      setMailbox(mb);
      onMailboxChange(mb);
      setAllowlistDirty(false);
    } catch {
      /* non-critical */
    } finally {
      setSavingAllowlist(false);
    }
  }

  async function deletePhoto(photoId: string) {
    setDeletingIds((prev) => new Set(prev).add(photoId));
    try {
      await foyerApi.deleteRoomCarePhoto(premiseId, photoId);
      setRoomPhotos((prev) => prev.filter((p) => p.photoId !== photoId));
    } catch {
      /* non-critical - just leave the thumbnail */
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(photoId);
        return next;
      });
    }
  }

  // Photo currently shown in the lightbox (derived from index).
  const lightboxPhoto =
    lightboxIndex != null ? (roomPhotos[lightboxIndex] ?? null) : null;

  const showPrevPhoto = () =>
    setLightboxIndex((i) =>
      i == null || roomPhotos.length === 0
        ? i
        : (i - 1 + roomPhotos.length) % roomPhotos.length
    );
  const showNextPhoto = () =>
    setLightboxIndex((i) =>
      i == null || roomPhotos.length === 0 ? i : (i + 1) % roomPhotos.length
    );

  // Delete the photo currently open in the lightbox, then advance to the
  // next one (or close if it was the last).
  async function deleteFromLightbox() {
    if (lightboxIndex == null) return;
    const photo = roomPhotos[lightboxIndex];
    if (!photo) return;
    await deletePhoto(photo.photoId);
    // roomPhotos still holds the pre-delete list inside this closure, so the
    // new length is length - 1; clamp the index into the shortened list.
    const newLen = roomPhotos.length - 1;
    setLightboxIndex(newLen <= 0 ? null : Math.min(lightboxIndex, newLen - 1));
  }

  // Arrow-key navigation while the lightbox is open.
  useEffect(() => {
    if (lightboxIndex == null) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "ArrowLeft") showPrevPhoto();
      else if (e.key === "ArrowRight") showNextPhoto();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxIndex, roomPhotos.length]);

  async function revoke() {
    setRevoking(true);
    try {
      await foyerApi.revokeRoomPhotoMailbox(premiseId, roomId);
      onMailboxChange(null);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not revoke");
      setRevoking(false);
      setConfirmRevoke(false);
    }
  }

  function formatDate(iso: string) {
    return new Date(iso).toLocaleDateString("sv-SE", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }

  function formatDateTime(iso: string) {
    return new Date(iso).toLocaleString("sv-SE", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  return (
    <Popover
      open
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{ paper: { sx: { p: 2, width: 420 } } }}
      data-testid={`room-photo-mailbox-popover-${roomId}`}
    >
      <Stack spacing={2}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Family photos
        </Typography>

        {/* ── No mailbox yet ─────────────────────────────────────── */}
        {!mailbox && (
          <>
            <Typography variant="body2" color="text.secondary">
              Activate a dedicated email address for this room. The
              resident&apos;s family emails photos here and they appear as a
              slideshow on the room TV within a minute.
            </Typography>
            {error && (
              <Alert severity="error" sx={{ py: 0 }}>
                {error}
              </Alert>
            )}
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button size="small" onClick={onClose}>
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => void activate()}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={14} /> : undefined}
              >
                Activate
              </Button>
            </Stack>
          </>
        )}

        {/* ── Mailbox exists ─────────────────────────────────────── */}
        {mailbox && (
          <>
            {/* Email address */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                px: 1.5,
                py: 1,
                bgcolor: "action.hover",
                borderRadius: 1,
                border: 1,
                borderColor: "divider",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  fontFamily:
                    'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
                  fontSize: "0.75rem",
                  wordBreak: "break-all",
                  color: mailbox.active ? "text.primary" : "text.disabled",
                }}
              >
                {mailbox.emailAddress}
              </Typography>
              <Tooltip title={copied ? "Copied!" : "Copy"}>
                <IconButton size="small" onClick={copyEmail}>
                  {copied ? (
                    <CheckIcon fontSize="small" color="success" />
                  ) : (
                    <ContentCopyIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </Stack>

            {/* Stats */}
            <Stack direction="row" spacing={3}>
              <Stack>
                <Typography variant="caption" color="text.secondary">
                  Photos in slideshow
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {photoCount}
                </Typography>
              </Stack>
              <Stack>
                <Typography variant="caption" color="text.secondary">
                  Last received
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {lastReceived ? formatDate(lastReceived) : "-"}
                </Typography>
              </Stack>
            </Stack>

            {/* Photo thumbnails */}
            {roomPhotos.length > 0 && (
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Photos ({roomPhotos.length})
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: 0.75,
                    maxHeight: 200,
                    overflowY: "auto",
                  }}
                >
                  {roomPhotos.map((photo, photoIdx) => (
                    <Box
                      key={photo.photoId}
                      sx={{
                        position: "relative",
                        aspectRatio: "1",
                        borderRadius: 1,
                        overflow: "hidden",
                        bgcolor: "action.hover",
                        border: 1,
                        borderColor:
                          photo.status === "approved"
                            ? "divider"
                            : photo.status === "pending"
                              ? "warning.light"
                              : "error.light",
                      }}
                    >
                      <Box
                        component="img"
                        src={photo.imageUrl}
                        alt={photo.caption ?? photo.fromName}
                        onClick={() => setLightboxIndex(photoIdx)}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                          cursor: "pointer",
                        }}
                      />
                      <Tooltip
                        title={
                          photo.status === "pending"
                            ? "Delete (pending)"
                            : photo.status === "rejected"
                              ? "Delete (rejected)"
                              : "Delete"
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={() => void deletePhoto(photo.photoId)}
                          disabled={deletingIds.has(photo.photoId)}
                          sx={{
                            position: "absolute",
                            top: 2,
                            right: 2,
                            width: 22,
                            height: 22,
                            bgcolor: "rgba(0,0,0,0.55)",
                            color: "#fff",
                            "&:hover": { bgcolor: "rgba(200,0,0,0.8)" },
                            p: 0,
                          }}
                        >
                          {deletingIds.has(photo.photoId) ? (
                            <CircularProgress
                              size={12}
                              sx={{ color: "#fff" }}
                            />
                          ) : (
                            <DeleteIcon sx={{ fontSize: 14 }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Box>
              </Stack>
            )}

            {/* Allowlist */}
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">
                Approved senders (one email per line, blank&nbsp;=&nbsp;anyone)
              </Typography>
              <TextField
                multiline
                minRows={2}
                maxRows={5}
                size="small"
                placeholder={"family@example.com\nrelative@example.com"}
                value={allowlistText}
                onChange={(e) => {
                  setAllowlistText(e.target.value);
                  setAllowlistDirty(true);
                }}
                inputProps={{ style: { fontSize: "0.8rem" } }}
              />
              {allowlistDirty && (
                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => void saveAllowlist()}
                    disabled={savingAllowlist}
                  >
                    {savingAllowlist ? "Saving…" : "Save"}
                  </Button>
                </Stack>
              )}
            </Stack>

            {/* Pause / Resume toggle */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack>
                <Typography variant="body2">
                  {mailbox.active ? "Accepting new photos" : "Paused"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {mailbox.active
                    ? "Turn off to stop new emails (existing photos stay)"
                    : "Resume to accept new emails again"}
                </Typography>
              </Stack>
              <Switch
                checked={mailbox.active}
                onChange={() => void toggleActive()}
                size="small"
                inputProps={
                  {
                    "aria-label": "Toggle photo sharing",
                    "data-testid": `mailbox-active-${roomId}`,
                  } as React.InputHTMLAttributes<HTMLInputElement>
                }
              />
            </Stack>

            {/* Moderation toggle */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack>
                <Typography variant="body2">
                  {mailbox.moderated
                    ? "Review before showing"
                    : "Show automatically"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {mailbox.moderated
                    ? "New photos wait in the moderation queue for staff approval"
                    : "New photos appear on the TV right away"}
                </Typography>
              </Stack>
              <Switch
                checked={mailbox.moderated}
                onChange={() => void toggleModerated()}
                size="small"
                inputProps={
                  {
                    "aria-label": "Toggle photo moderation",
                    "data-testid": `mailbox-moderated-${roomId}`,
                  } as React.InputHTMLAttributes<HTMLInputElement>
                }
              />
            </Stack>

            {error && (
              <Alert severity="error" sx={{ py: 0 }}>
                {error}
              </Alert>
            )}

            {/* Revoke */}
            {!confirmRevoke ? (
              <Stack direction="row" justifyContent="space-between" pt={0.5}>
                <Button
                  size="small"
                  color="error"
                  onClick={() => setConfirmRevoke(true)}
                >
                  Revoke photo sharing
                </Button>
                <Button size="small" onClick={onClose}>
                  Close
                </Button>
              </Stack>
            ) : (
              <Stack spacing={1}>
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  This archives all current photos and disconnects the email
                  address. Future emails to this address will be silently
                  dropped.
                </Alert>
                <Stack direction="row" justifyContent="flex-end" spacing={1}>
                  <Button
                    size="small"
                    onClick={() => setConfirmRevoke(false)}
                    disabled={revoking}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    onClick={() => void revoke()}
                    disabled={revoking}
                    startIcon={
                      revoking ? <CircularProgress size={14} /> : undefined
                    }
                  >
                    {revoking ? "Revoking…" : "Yes, revoke"}
                  </Button>
                </Stack>
              </Stack>
            )}
          </>
        )}
      </Stack>

      {/* ── Large-format photo viewer ──────────────────────────────── */}
      <Dialog
        open={!!lightboxPhoto}
        onClose={() => setLightboxIndex(null)}
        maxWidth="md"
        fullWidth
        data-testid="care-photo-lightbox"
      >
        {lightboxPhoto && (
          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ position: "relative", bgcolor: "#000" }}>
              <Box
                component="img"
                src={lightboxPhoto.imageUrl}
                alt={lightboxPhoto.caption ?? lightboxPhoto.fromName}
                sx={{
                  display: "block",
                  width: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                }}
              />
              <IconButton
                onClick={() => setLightboxIndex(null)}
                aria-label="Close"
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  bgcolor: "rgba(0,0,0,0.55)",
                  color: "#fff",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>

              {/* Prev / Next navigation (only when >1 photo) */}
              {roomPhotos.length > 1 && (
                <>
                  <IconButton
                    onClick={showPrevPhoto}
                    aria-label="Previous photo"
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: 8,
                      transform: "translateY(-50%)",
                      bgcolor: "rgba(0,0,0,0.55)",
                      color: "#fff",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                    }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  <IconButton
                    onClick={showNextPhoto}
                    aria-label="Next photo"
                    sx={{
                      position: "absolute",
                      top: "50%",
                      right: 8,
                      transform: "translateY(-50%)",
                      bgcolor: "rgba(0,0,0,0.55)",
                      color: "#fff",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                    }}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                  <Typography
                    variant="caption"
                    sx={{
                      position: "absolute",
                      bottom: 8,
                      left: "50%",
                      transform: "translateX(-50%)",
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      bgcolor: "rgba(0,0,0,0.55)",
                      color: "#fff",
                    }}
                  >
                    {(lightboxIndex ?? 0) + 1} / {roomPhotos.length}
                  </Typography>
                </>
              )}
            </Box>
            <Stack spacing={1.5} sx={{ p: 2.5 }}>
              <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                spacing={1}
              >
                <Stack spacing={1}>
                  {lightboxPhoto.caption && (
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {lightboxPhoto.caption}
                    </Typography>
                  )}
                  <Chip
                    size="small"
                    label={
                      lightboxPhoto.status === "approved"
                        ? "Approved"
                        : lightboxPhoto.status === "pending"
                          ? "Pending"
                          : "Rejected"
                    }
                    color={
                      lightboxPhoto.status === "approved"
                        ? "success"
                        : lightboxPhoto.status === "pending"
                          ? "warning"
                          : "error"
                    }
                    variant="outlined"
                    sx={{ alignSelf: "flex-start" }}
                  />
                </Stack>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  startIcon={
                    lightboxPhoto && deletingIds.has(lightboxPhoto.photoId) ? (
                      <CircularProgress size={14} />
                    ) : (
                      <DeleteIcon />
                    )
                  }
                  onClick={() => void deleteFromLightbox()}
                  disabled={
                    !!lightboxPhoto && deletingIds.has(lightboxPhoto.photoId)
                  }
                  sx={{ flexShrink: 0 }}
                >
                  Delete
                </Button>
              </Stack>
              <Divider />
              <Stack spacing={1.25}>
                <PhotoMetaRow
                  label="From"
                  value={
                    lightboxPhoto.fromName
                      ? `${lightboxPhoto.fromName} · ${lightboxPhoto.fromEmail}`
                      : lightboxPhoto.fromEmail
                  }
                />
                {lightboxPhoto.subject && (
                  <PhotoMetaRow label="Subject" value={lightboxPhoto.subject} />
                )}
                <PhotoMetaRow
                  label="Received"
                  value={formatDateTime(lightboxPhoto.receivedAt)}
                />
                {lightboxPhoto.expiresAt && (
                  <PhotoMetaRow
                    label="Expires"
                    value={formatDateTime(lightboxPhoto.expiresAt)}
                  />
                )}
              </Stack>
            </Stack>
          </DialogContent>
        )}
      </Dialog>
    </Popover>
  );
}

/** One label/value line in the photo lightbox metadata panel. */
function PhotoMetaRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" spacing={2}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ minWidth: 88, flexShrink: 0, pt: 0.25 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
        {value}
      </Typography>
    </Stack>
  );
}

/**
 * Tiny popover that edits a single room's `integrationId`. Kept out-of-
 * line so the row stays compact and rooms without a value don't carry
 * an extra empty input.
 */
function IntegrationIdPopover({
  anchorEl,
  room,
  onClose,
  onSave,
}: {
  anchorEl: HTMLElement;
  room: Room;
  onClose: () => void;
  onSave: (next: string) => void;
}) {
  const [value, setValue] = useState(room.integrationId ?? "");
  const inputRef = useRef<HTMLInputElement | null>(null);

  return (
    <Popover
      open
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{
        paper: {
          sx: { p: 2, width: 340 },
        },
      }}
      data-testid={`room-more-popover-${room.id}`}
    >
      <Stack spacing={1.5}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Integration token
        </Typography>
        <Typography variant="caption" color="text.secondary">
          The token the integration issued for this room. VilMer activities read
          it here — the same token often covers a whole ward, so paste it into
          every room on that ward. Never sent to the TV: the server holds it and
          fetches on the room&apos;s behalf. Also fills the{" "}
          <Box
            component="code"
            sx={{
              fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
              bgcolor: "action.hover",
              px: 0.5,
              borderRadius: 0.5,
            }}
          >
            {"{integrationId}"}
          </Box>{" "}
          placeholder in Embedded content components.
        </Typography>
        <TextField
          autoFocus
          inputRef={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onSave(value.trim());
            } else if (e.key === "Escape") {
              onClose();
            }
          }}
          size="small"
          fullWidth
          placeholder="e.g. 08cba4089f38e5dea12369843a02c5ba"
          inputProps={{
            "data-testid": `room-integration-id-${room.id}`,
            style: {
              fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
            },
          }}
        />
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          {room.integrationId && (
            <Button
              size="small"
              color="inherit"
              onClick={() => onSave("")}
              data-testid={`room-integration-clear-${room.id}`}
            >
              Clear
            </Button>
          )}
          <Button size="small" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => onSave(value.trim())}
            data-testid={`room-integration-save-${room.id}`}
          >
            Save
          </Button>
        </Stack>
      </Stack>
    </Popover>
  );
}

// `Chip` is imported above for future extension (room-type chip in
// table cells). Silence unused-import warning if tree-shaking trims it.
void Chip;
